// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import ebanxSrc from './ebanx.svg';
import { theme } from '../../theme';
import { Separator, Margin, Padding, Empty } from '../../atoms';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';

type Props = {
  children: Node,
};

export function PageTemplate({ children, ...rest }: Props): Node {
  return (
    <Article {...rest}>
      <Main>{children}</Main>
      <InformationFooter />
    </Article>
  );
}

function InformationFooter(): Node {
  const incomingPayment = useIncomingPayment();
  const incomingPaymentStatusEligible = incomingPayment.status === 'OP' || incomingPayment.status === 'PE'|| incomingPayment.status === 'CO' ;
  const isMPConnect = incomingPayment.payment_type_code === 'mpconnect';

  if (incomingPaymentStatusEligible) {
    return (
      <Footer>
        <Margin value="2em 1em">
          {!isMPConnect && (
            <p>
              <SafePaymentMessage />
              <EbanxLogo src={ebanxSrc} alt="EBANX" />
            </p>)
          }
          <Padding value=".5em calc(50% - 6em)">
            <Separator size="1px" pattern="solid" />
          </Padding>
          <p>
            <UberPartnerMessage />
          </p>
        </Margin>
      </Footer>
    );
  }

  return <Empty />;
}

function SafePaymentMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Pagamento seguro via ';

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
      return 'Pago seguro vía ';

    default:
      throw new Error(`Uber Partner Message: not implemented for country: ${country}`);
  }
}

function UberPartnerMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Parceiro oficial da Uber';

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
      return 'Proveedor oficial de Uber';

    default:
      throw new Error(`Safe Payment Message: not implemented for country: ${country}`);
  }
}

const Article = styled.article`
  min-height: 100vh;
  height: 100%;
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-bottom: 1px solid ${theme.colors.gray(90)};
  margin: auto;
  max-width: 425px;
  padding-top: 1em;
  > * {
    width: calc(100% - 2em);
    margin: 0.7em auto;
  }
  > ${Separator} {
    width: 100%;
  }
`;

const Footer = styled.footer`
  color: ${theme.colors.gray(20, 0.6)};
  text-align: center;
`;

const EbanxLogo = styled.img`
  font-size: 0.9em;
  height: 1em;
  vertical-align: middle;
`;
